import { changed, noOp, PaintStrokeCommand, rejected, type BrushSettings, type Command, type CommandExecution, type StrokePoint } from './commands';
import type { Point } from './geometry';
import { damage } from './damage';
import { createId, type EditorDocument, type LayerId } from './model';
import { RasterTileStore } from './tiles';

export const PERSPECTIVE_PLANES_EXTENSION = 'maipo:perspective-planes';
export const ACTIVE_PERSPECTIVE_PLANE_EXTENSION = 'maipo:active-perspective-plane';

export type Quad = readonly [Point, Point, Point, Point];
export type Homography = readonly [number, number, number, number, number, number, number, number, number];
export type PerspectiveGridMode = 'one-point' | 'two-point' | 'three-point';

export interface PerspectivePlane {
  id: string;
  name: string;
  mode: PerspectiveGridMode;
  corners: Quad;
}

export function homographyFromUnitSquare([topLeft, topRight, bottomRight, bottomLeft]: Quad): Homography {
  const dx1 = topRight.x - bottomRight.x;
  const dx2 = bottomLeft.x - bottomRight.x;
  const dx3 = topLeft.x - topRight.x + bottomRight.x - bottomLeft.x;
  const dy1 = topRight.y - bottomRight.y;
  const dy2 = bottomLeft.y - bottomRight.y;
  const dy3 = topLeft.y - topRight.y + bottomRight.y - bottomLeft.y;
  const denominator = dx1 * dy2 - dx2 * dy1;
  if (Math.abs(dx3) < 1e-10 && Math.abs(dy3) < 1e-10) {
    return [topRight.x - topLeft.x, bottomLeft.x - topLeft.x, topLeft.x, topRight.y - topLeft.y, bottomLeft.y - topLeft.y, topLeft.y, 0, 0, 1];
  }
  if (Math.abs(denominator) < 1e-10) throw new Error('Perspective plane is degenerate.');
  const g = (dx3 * dy2 - dx2 * dy3) / denominator;
  const h = (dx1 * dy3 - dx3 * dy1) / denominator;
  return [
    topRight.x - topLeft.x + g * topRight.x,
    bottomLeft.x - topLeft.x + h * bottomLeft.x,
    topLeft.x,
    topRight.y - topLeft.y + g * topRight.y,
    bottomLeft.y - topLeft.y + h * bottomLeft.y,
    topLeft.y,
    g,
    h,
    1,
  ];
}

export function invertHomography(matrix: Homography): Homography {
  const [a, b, c, d, e, f, g, h, i] = matrix;
  const determinant = a * (e * i - f * h) - b * (d * i - f * g) + c * (d * h - e * g);
  if (Math.abs(determinant) < 1e-12) throw new Error('Perspective plane is not invertible.');
  return [
    (e * i - f * h) / determinant,
    (c * h - b * i) / determinant,
    (b * f - c * e) / determinant,
    (f * g - d * i) / determinant,
    (a * i - c * g) / determinant,
    (c * d - a * f) / determinant,
    (d * h - e * g) / determinant,
    (b * g - a * h) / determinant,
    (a * e - b * d) / determinant,
  ];
}

export function applyHomography(matrix: Homography, point: Point): Point {
  const denominator = matrix[6] * point.x + matrix[7] * point.y + matrix[8];
  if (Math.abs(denominator) < 1e-12) return { x: Number.NaN, y: Number.NaN };
  return {
    x: (matrix[0] * point.x + matrix[1] * point.y + matrix[2]) / denominator,
    y: (matrix[3] * point.x + matrix[4] * point.y + matrix[5]) / denominator,
  };
}

export function validPerspectiveQuad(corners: Quad): boolean {
  if (!corners.every((point) => Number.isFinite(point.x) && Number.isFinite(point.y))) return false;
  const crosses = corners.map((point, index) => {
    const next = corners[(index + 1) % 4]!;
    const after = corners[(index + 2) % 4]!;
    return (next.x - point.x) * (after.y - next.y) - (next.y - point.y) * (after.x - next.x);
  });
  return crosses.every((value) => value > 0.001) || crosses.every((value) => value < -0.001);
}

export function perspectiveGridLines(plane: PerspectivePlane, divisions = 8): Point[][] {
  const transform = homographyFromUnitSquare(plane.corners);
  const lines: Point[][] = [];
  for (let index = 0; index <= divisions; index += 1) {
    const amount = index / divisions;
    lines.push(Array.from({ length: divisions + 1 }, (_, step) => applyHomography(transform, { x: amount, y: step / divisions })));
    lines.push(Array.from({ length: divisions + 1 }, (_, step) => applyHomography(transform, { x: step / divisions, y: amount })));
  }
  return lines;
}

export function perspectiveCloneMapper(corners: Quad, sourcePoint: Point, destinationPoint: Point): (point: Point) => Point {
  const transform = homographyFromUnitSquare(corners);
  const inverse = invertHomography(transform);
  const sourceUnit = applyHomography(inverse, sourcePoint);
  const destinationUnit = applyHomography(inverse, destinationPoint);
  const offset = { x: sourceUnit.x - destinationUnit.x, y: sourceUnit.y - destinationUnit.y };
  return (point) => {
    const unit = applyHomography(inverse, point);
    const sampled = { x: unit.x + offset.x, y: unit.y + offset.y };
    if (unit.x < 0 || unit.y < 0 || unit.x > 1 || unit.y > 1 || sampled.x < 0 || sampled.y < 0 || sampled.x > 1 || sampled.y > 1) {
      return { x: Number.NaN, y: Number.NaN };
    }
    return applyHomography(transform, sampled);
  };
}

export class PerspectiveCloneStampCommand extends PaintStrokeCommand {
  constructor(
    layerId: LayerId,
    settings: BrushSettings,
    firstPoint: StrokePoint,
    source: RasterTileStore,
    corners: Quad,
    sourcePoint: Point,
    destinationPoint: Point,
  ) {
    super(
      layerId,
      { ...settings, mode: 'paint' },
      firstPoint,
      source,
      'Perspective clone stroke',
      { x: 0, y: 0 },
      'clone',
      perspectiveCloneMapper(corners, sourcePoint, destinationPoint),
    );
  }
}

function validPlane(value: unknown): value is Omit<PerspectivePlane, 'mode'> & { mode?: PerspectiveGridMode } {
  if (!value || typeof value !== 'object') return false;
  const plane = value as Partial<PerspectivePlane>;
  return typeof plane.id === 'string' && typeof plane.name === 'string' && Array.isArray(plane.corners) && plane.corners.length === 4 && validPerspectiveQuad(plane.corners as unknown as Quad);
}

export function perspectivePlanes(document: EditorDocument): PerspectivePlane[] {
  const source = document.extensions[PERSPECTIVE_PLANES_EXTENSION];
  return Array.isArray(source) ? source.filter(validPlane).map((plane) => ({
    ...structuredClone(plane),
    mode: plane.mode === 'one-point' || plane.mode === 'three-point' ? plane.mode : 'two-point',
  })) : [];
}

export function activePerspectivePlane(document: EditorDocument): PerspectivePlane | undefined {
  const planes = perspectivePlanes(document);
  const activeId = document.extensions[ACTIVE_PERSPECTIVE_PLANE_EXTENSION];
  return planes.find((plane) => plane.id === activeId) ?? planes[0];
}

export function perspectivePresetQuad(mode: PerspectiveGridMode, width: number, height: number): Quad {
  if (mode === 'one-point') return [
    { x: width * 0.35, y: height * 0.26 },
    { x: width * 0.65, y: height * 0.26 },
    { x: width * 0.84, y: height * 0.86 },
    { x: width * 0.16, y: height * 0.86 },
  ];
  if (mode === 'three-point') return [
    { x: width * 0.27, y: height * 0.16 },
    { x: width * 0.78, y: height * 0.28 },
    { x: width * 0.66, y: height * 0.84 },
    { x: width * 0.18, y: height * 0.70 },
  ];
  return [
    { x: width * 0.20, y: height * 0.30 },
    { x: width * 0.76, y: height * 0.18 },
    { x: width * 0.86, y: height * 0.76 },
    { x: width * 0.24, y: height * 0.88 },
  ];
}

export function createPerspectivePlane(corners: Quad, name = 'Perspective Grid', mode: PerspectiveGridMode = 'two-point'): PerspectivePlane {
  if (!validPerspectiveQuad(corners)) throw new Error('The perspective plane must be a convex, non-folded quadrilateral.');
  return { id: createId('plane'), name, mode, corners: structuredClone(corners) };
}

export class SetPerspectivePlanesCommand implements Command {
  readonly label = 'Update perspective grids';
  private before?: PerspectivePlane[];
  private beforeActiveId: string | undefined;
  constructor(private readonly planes: readonly PerspectivePlane[], private readonly activeId?: string) {}
  retainedBytes(): number { return 256 + JSON.stringify(this.planes).length * 2 + JSON.stringify(this.before ?? []).length * 2; }
  execute(document: EditorDocument): CommandExecution {
    if (!this.before) {
      this.before = perspectivePlanes(document);
      this.beforeActiveId = activePerspectivePlane(document)?.id;
    }
    if (this.planes.length > 8) return rejected('Perspective Grid is limited to eight planes per document.');
    if (new Set(this.planes.map((plane) => plane.id)).size !== this.planes.length || this.planes.some((plane) => !validPerspectiveQuad(plane.corners))) return rejected('Perspective Grid planes must have unique IDs and valid convex corners.');
    const nextActiveId = this.planes.find((plane) => plane.id === this.activeId)?.id
      ?? this.planes.find((plane) => plane.id === this.beforeActiveId)?.id
      ?? this.planes[0]?.id;
    if (JSON.stringify(this.before) === JSON.stringify(this.planes) && this.beforeActiveId === nextActiveId) return noOp();
    if (this.planes.length) document.extensions[PERSPECTIVE_PLANES_EXTENSION] = structuredClone(this.planes);
    else delete document.extensions[PERSPECTIVE_PLANES_EXTENSION];
    if (nextActiveId) document.extensions[ACTIVE_PERSPECTIVE_PLANE_EXTENSION] = nextActiveId;
    else delete document.extensions[ACTIVE_PERSPECTIVE_PLANE_EXTENSION];
    return changed(this.damage());
  }
  undo(document: EditorDocument): void {
    if (this.before?.length) document.extensions[PERSPECTIVE_PLANES_EXTENSION] = structuredClone(this.before);
    else delete document.extensions[PERSPECTIVE_PLANES_EXTENSION];
    if (this.beforeActiveId) document.extensions[ACTIVE_PERSPECTIVE_PLANE_EXTENSION] = this.beforeActiveId;
    else delete document.extensions[ACTIVE_PERSPECTIVE_PLANE_EXTENSION];
  }
  damage() { return damage({ fullDocument: true }); }
}

function sourcePixel(store: RasterTileStore, x: number, y: number): readonly [number, number, number, number] {
  const integerX = Math.round(x);
  const integerY = Math.round(y);
  if (integerX < 0 || integerY < 0 || integerX >= store.width || integerY >= store.height) return [0, 0, 0, 0];
  const tileX = Math.floor(integerX / store.tileSize);
  const tileY = Math.floor(integerY / store.tileSize);
  const tile = store.getTile(tileX, tileY);
  if (!tile) return [0, 0, 0, 0];
  const dimensions = store.tileDimensions(tileX, tileY);
  const index = ((integerY - tileY * store.tileSize) * dimensions.width + integerX - tileX * store.tileSize) * 4;
  return [tile[index] ?? 0, tile[index + 1] ?? 0, tile[index + 2] ?? 0, tile[index + 3] ?? 0];
}

/** Maps the full source rectangle into a perspective quad in layer-local pixels. */
export function warpRasterToPerspectivePlane(source: RasterTileStore, corners: Quad, maxPixels = 16_000_000): RasterTileStore {
  if (!validPerspectiveQuad(corners)) throw new Error('The perspective plane is invalid.');
  if (source.width * source.height > maxPixels) throw new Error('Perspective mapping is limited to 16 megapixels per layer.');
  const inverse = invertHomography(homographyFromUnitSquare(corners));
  const output = new RasterTileStore(source.width, source.height, source.tileSize);
  for (let y = 0; y < source.height; y += 1) for (let x = 0; x < source.width; x += 1) {
    const unit = applyHomography(inverse, { x: x + 0.5, y: y + 0.5 });
    if (unit.x < 0 || unit.y < 0 || unit.x > 1 || unit.y > 1) continue;
    const color = sourcePixel(source, unit.x * (source.width - 1), unit.y * (source.height - 1));
    if (color[3] === 0) continue;
    const tileX = Math.floor(x / output.tileSize);
    const tileY = Math.floor(y / output.tileSize);
    const tile = output.getTile(tileX, tileY, true);
    if (!tile) continue;
    const dimensions = output.tileDimensions(tileX, tileY);
    const index = ((y - tileY * output.tileSize) * dimensions.width + x - tileX * output.tileSize) * 4;
    tile.set(color, index);
  }
  return output;
}
